import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import ReactAvatar from 'react-avatar';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { FaHashtag as FaHashtagRaw, FaSearch as FaSearchRaw } from 'react-icons/fa';
import { supabase } from '../services/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaHashtag: any = FaHashtagRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaSearch: any = FaSearchRaw;

const Page = styled.div`
  min-height: 100%;
  max-width: 980px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HeroCard = styled.section`
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--surface) 96%, transparent) 0%,
    color-mix(in srgb, var(--surfaceVariant, var(--surface)) 84%, transparent) 100%
  );
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  border-radius: 24px;
  padding: clamp(1.1rem, 2.5vw, 1.5rem);
  display: grid;
  gap: 0.9rem;
  box-shadow: 0 14px 44px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.4rem, 2.4vw, 1.8rem);
  color: var(--onSurface);
`;

const Subtitle = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.92rem;
`;

const SearchInput = styled.input`
  width: 100%;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.85rem 1rem;
  font-size: 1rem;

  &:focus {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
`;

const Section = styled.section`
  display: grid;
  gap: 0.55rem;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 0.82rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--onSurfaceVariant);
`;

const ResultList = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const ResultCard = styled.button`
  width: 100%;
  text-align: left;
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  background: var(--surface);
  border-radius: 16px;
  padding: 0.85rem 1rem;
  display: flex;
  gap: 0.85rem;
  align-items: center;
  cursor: pointer;
  color: inherit;

  &:hover {
    border-color: color-mix(in srgb, var(--primary) 45%, var(--outline));
  }
`;

const ResultLink = styled(Link)`
  width: 100%;
  text-align: left;
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  background: var(--surface);
  border-radius: 16px;
  padding: 0.85rem 1rem;
  display: flex;
  gap: 0.85rem;
  align-items: center;
  text-decoration: none;
  color: inherit;

  &:hover {
    border-color: color-mix(in srgb, var(--primary) 45%, var(--outline));
  }
`;

const ResultBody = styled.div`
  display: grid;
  gap: 0.2rem;
  min-width: 0;
`;

const ResultTitle = styled.div`
  font-weight: 650;
  color: var(--onSurface);
  overflow-wrap: anywhere;
`;

const ResultMeta = styled.div`
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

const Badge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: color-mix(in srgb, var(--primary) 18%, transparent);
  color: var(--primary);
  flex-shrink: 0;
`;

const Empty = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
`;

const ErrorText = styled.p`
  margin: 0;
  color: #b42318;
  font-size: 0.9rem;
`;

type SearchPayload = {
  people: any[];
  hashtags: Array<{ id: string; tag: string; use_count: number }>;
  posts: any[];
  comments: any[];
  group_chat: any[];
  query: string;
};

const preview = (value: unknown, max = 140) => {
  const text = typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

export default function GlobalSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('q') || '';
  const [query, setQuery] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SearchPayload>({
    people: [],
    hashtags: [],
    posts: [],
    comments: [],
    group_chat: [],
    query: '',
  });

  useEffect(() => {
    setQuery(initial);
  }, [initial]);

  useEffect(() => {
    const handle = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: rpcError } = await supabase.rpc('global_search', {
          p_query: query,
          p_limit_per_section: 8,
        });
        if (rpcError) throw rpcError;
        const payload = data && typeof data === 'object' ? data : {};
        setResults({
          people: Array.isArray(payload.people) ? payload.people : [],
          hashtags: Array.isArray(payload.hashtags) ? payload.hashtags : [],
          posts: Array.isArray(payload.posts) ? payload.posts : [],
          comments: Array.isArray(payload.comments) ? payload.comments : [],
          group_chat: Array.isArray(payload.group_chat) ? payload.group_chat : [],
          query: typeof payload.query === 'string' ? payload.query : query,
        });
      } catch (err: any) {
        setError(err?.message || 'Search failed');
        setResults({
          people: [],
          hashtags: [],
          posts: [],
          comments: [],
          group_chat: [],
          query,
        });
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(handle);
  }, [query]);

  const hasQuery = query.trim().length > 0;

  const sections = useMemo(() => {
    const list: Array<{ key: string; title: string; empty?: string; content: React.ReactNode }> = [];

    if (hasQuery && results.people.length) {
      list.push({
        key: 'people',
        title: 'People',
        content: (
          <ResultList>
            {results.people.map((person) => (
              <ResultLink key={person.id} to={`/members/${person.id}`}>
                <ReactAvatar
                  name={person.full_name || 'Member'}
                  src={person.avatar || undefined}
                  size="40"
                  round
                />
                <ResultBody>
                  <ResultTitle>{person.full_name || 'Member'}</ResultTitle>
                  {person.title ? <ResultMeta>{person.title}</ResultMeta> : null}
                </ResultBody>
              </ResultLink>
            ))}
          </ResultList>
        ),
      });
    }

    list.push({
      key: 'hashtags',
      title: hasQuery ? 'Hashtags' : 'Popular hashtags',
      empty: 'No hashtags yet. Try posting with #church.',
      content: results.hashtags.length ? (
        <ResultList>
          {results.hashtags.map((tag) => (
            <ResultCard
              key={tag.id}
              type="button"
              onClick={() => {
                const next = `#${tag.tag}`;
                setQuery(next);
                setSearchParams({ q: next });
              }}
            >
              <Badge>
                <FaHashtag size={16} />
              </Badge>
              <ResultBody>
                <ResultTitle>#{tag.tag}</ResultTitle>
                <ResultMeta>
                  {tag.use_count} {tag.use_count === 1 ? 'use' : 'uses'}
                </ResultMeta>
              </ResultBody>
            </ResultCard>
          ))}
        </ResultList>
      ) : null,
    });

    if (hasQuery && results.posts.length) {
      list.push({
        key: 'posts',
        title: 'Posts',
        content: (
          <ResultList>
            {results.posts.map((post) => {
              const to =
                post.source_type === 'group_post' && post.group_id
                  ? `/groups/${post.group_id}/posts/${post.id}`
                  : `/community/posts/${post.id}`;
              return (
                <ResultLink key={`${post.source_type}-${post.id}`} to={to}>
                  <Badge>
                    <FaSearch size={14} />
                  </Badge>
                  <ResultBody>
                    <ResultTitle>{preview(post.content) || 'Untitled post'}</ResultTitle>
                    <ResultMeta>
                      {post.author_name || 'Someone'}
                      {post.group_name ? ` · ${post.group_name}` : ''}
                    </ResultMeta>
                  </ResultBody>
                </ResultLink>
              );
            })}
          </ResultList>
        ),
      });
    }

    if (hasQuery && results.comments.length) {
      list.push({
        key: 'comments',
        title: 'Comments',
        content: (
          <ResultList>
            {results.comments.map((comment) => {
              const to =
                comment.parent_source_type === 'group_post' && comment.group_id && comment.parent_post_id
                  ? `/groups/${comment.group_id}/posts/${comment.parent_post_id}`
                  : comment.parent_post_id
                    ? `/community/posts/${comment.parent_post_id}`
                    : '/search';
              return (
                <ResultLink key={`${comment.source_type}-${comment.id}`} to={to}>
                  <Badge>
                    <FaSearch size={14} />
                  </Badge>
                  <ResultBody>
                    <ResultTitle>{preview(comment.content) || 'Comment'}</ResultTitle>
                    <ResultMeta>
                      {comment.author_name || 'Someone'}
                      {comment.group_name ? ` · ${comment.group_name}` : ''}
                    </ResultMeta>
                  </ResultBody>
                </ResultLink>
              );
            })}
          </ResultList>
        ),
      });
    }

    if (hasQuery && results.group_chat.length) {
      list.push({
        key: 'chat',
        title: 'Group chat',
        content: (
          <ResultList>
            {results.group_chat.map((message) => {
              const to =
                message.group_id && message.chat_id
                  ? `/groups/${message.group_id}/chats/${message.chat_id}`
                  : '/groups';
              return (
                <ResultLink key={message.id} to={to}>
                  <Badge>
                    <FaSearch size={14} />
                  </Badge>
                  <ResultBody>
                    <ResultTitle>{preview(message.content) || 'Chat message'}</ResultTitle>
                    <ResultMeta>
                      {message.author_name || 'Someone'}
                      {message.chat_name ? ` · ${message.chat_name}` : ''}
                      {message.group_name ? ` · ${message.group_name}` : ''}
                    </ResultMeta>
                  </ResultBody>
                </ResultLink>
              );
            })}
          </ResultList>
        ),
      });
    }

    return list;
  }, [results, hasQuery, setSearchParams]);

  return (
    <Page>
      <HeroCard>
        <div>
          <Title>Search</Title>
          <Subtitle>Find people, posts, comments, group chat, and #hashtags across the community.</Subtitle>
        </div>
        <SearchInput
          value={query}
          onChange={(event) => {
            const next = event.target.value;
            setQuery(next);
            if (next.trim()) {
              setSearchParams({ q: next });
            } else {
              setSearchParams({});
            }
          }}
          placeholder="Search people, posts, #hashtags"
          autoFocus
        />
        {loading ? <Subtitle>Searching…</Subtitle> : null}
        {error ? <ErrorText>{error}</ErrorText> : null}
      </HeroCard>

      {sections.map((section) => (
        <Section key={section.key}>
          <SectionTitle>{section.title}</SectionTitle>
          {section.content || <Empty>{section.empty || 'No results'}</Empty>}
        </Section>
      ))}

      {hasQuery &&
      !loading &&
      !results.people.length &&
      !results.hashtags.length &&
      !results.posts.length &&
      !results.comments.length &&
      !results.group_chat.length ? (
        <Empty>No matches found. Try a name, a word, or a #hashtag.</Empty>
      ) : null}
    </Page>
  );
}
